import { Branch } from '../../../../dorian-shared/types/branch/Branch';
import { TEpisodeTextImportCommands } from '../../../../services/EpisodeTextService/types/episodeTextImportCommandTypes';
import { EpisodeTextEntityType } from '../../../../services/EpisodeTextService/types/episodeTextImportTypes';
import { EpisodeTextImportNode } from '../../../../services/EpisodeTextService/models/EpisodeTextImportNode';
import { EpisodeTextImportStep } from '../../../../services/EpisodeTextService/models/EpisodeTextImportStep';

import { MemoryDTO } from '../../Book/MemoryBank/memoryBankTypes';

// --- Search mode types ---

export type SearchMode = 'plain' | 'regex';

/** Structured search history entry persisted to localStorage. */
export type SearchHistoryEntry = {
  query: string;
  mode: SearchMode;
  caseSensitive: boolean;
};

export type SelectiveEditModalProps = {
  show: boolean;
  episodeId: number;
  bookId: number;
  preloadedNodes?: Branch[];
  memoryBank?: MemoryDTO[];
  endpointVersion?: 'v1' | 'v2' | 'v3';
  /** When set, Selective Edit opens scrolled to this branch and resets filters. */
  initialBranchId?: number | null;
  onHide: () => void;
  onSave?: () => void;
  onNavigateToNode?: (nodeId: number) => void;
  isReadOnly?: boolean;
};

// --- CSV-based editing model types ---

export type CsvEditRow = {
  id: string;
  nodeOrSpeaker: string;
  text: string;
  description: string;
  type: EpisodeTextEntityType | undefined;
  errors: string[];
  commands: TEpisodeTextImportCommands;
  isLastStep?: boolean;
};

export type CsvEditNodeGroup = {
  nodeRow: CsvEditRow;
  stepRows: CsvEditRow[];
  searchableText: string;
  /** Node name only (lowercased) — used by prefix filter `node:`. */
  nodeNameText: string;
  /** Concatenated step text + descriptions (no node name) — used by prefix filter `step:`. */
  stepContentText: string;
  /** Concatenated speaker/character names from steps — used by prefix filter `speaker:`. */
  speakerText: string;
};

// --- Smart filter token types ---

export type FilterPrefix = 'node' | 'step' | 'type' | 'speaker' | 'general';

export type FilterToken = {
  id: string;
  prefix: FilterPrefix;
  value: string;
  raw: string;
};

export type EditableField = 'nodeOrSpeaker' | 'text' | 'description';

export type ColumnEditability = {
  speaker: boolean | 'partial';
  text: boolean | 'partial';
  description: boolean | 'partial';
};

export const STEP_EDITABILITY: Record<EpisodeTextEntityType, ColumnEditability> = {
  [EpisodeTextEntityType.Node]: { speaker: true, text: false, description: 'partial' },
  [EpisodeTextEntityType.Dialogue]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Narration]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Thinking]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.thoughtful]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Reaction]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Chat]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Answer]: { speaker: true, text: true, description: 'partial' },
  [EpisodeTextEntityType.Remember]: { speaker: false, text: true, description: true },
  // Random: roll (AD_FIELD) is diffed via deepEqual in buildEditContextPayload.
  // speaker: false — validator rejects speaker column; text: true — #random command lives in text column;
  // description: true — #random command can also be in description column (scope includes both).
  [EpisodeTextEntityType.Random]: { speaker: false, text: true, description: true },
  // GotoFromNode represents branch.gotoBranchId — not a BranchStep, no own ID.
  // Read-only: editing goto target requires a reverse branchNames lookup and a dedicated
  // save mechanism (branch attribute patch, not step patch). TODO: enable when save is implemented.
  [EpisodeTextEntityType.GotoFromNode]: { speaker: false, text: false, description: false },
  [EpisodeTextEntityType.Check]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Ending]: { speaker: true, text: true, description: true },
  [EpisodeTextEntityType.Choice]: { speaker: true, text: 'partial', description: true },
  [EpisodeTextEntityType.CheckConditionList]: { speaker: false, text: false, description: false },
  [EpisodeTextEntityType.CheckCondition]: { speaker: false, text: 'partial', description: 'partial' },
  [EpisodeTextEntityType.EmptyStep]: { speaker: false, text: false, description: false },
  [EpisodeTextEntityType.Skip]: { speaker: false, text: false, description: false },
  [EpisodeTextEntityType.Video]: { speaker: false, text: false, description: false },
  [EpisodeTextEntityType.Achieve]: { speaker: false, text: false, description: false },
};

/**
 * True if editing `field` on a step that changed from `oldType` to `newType`
 * would trap the user — the field's editability downgrades so they can't undo.
 *
 * Editability levels: true > 'partial' > false
 * Any downgrade on the edited field is a trap (e.g. true→partial, true→false, partial→false).
 */
export function isEditabilityDowngrade(
  oldType: EpisodeTextEntityType,
  newType: EpisodeTextEntityType,
  field: EditableField,
): boolean {
  if (oldType === newType) return false;
  const oldE = STEP_EDITABILITY[oldType];
  const newE = STEP_EDITABILITY[newType];
  if (!oldE || !newE) return true; // unknown type → block
  const rank = (v: boolean | 'partial'): number => (v === true ? 2 : v === 'partial' ? 1 : 0);
  const editableFieldKey = field === 'nodeOrSpeaker' ? 'speaker' : field;
  return rank(newE[editableFieldKey]) < rank(oldE[editableFieldKey]);
}

// Field mapping: EditableField names do NOT match model property names
type StringKeysOf<T> = { [K in keyof T]: T[K] extends string ? K : never }[keyof T];

export const NODE_FIELD_MAP: Record<EditableField, StringKeysOf<EpisodeTextImportNode>> = {
  nodeOrSpeaker: 'title',
  text: 'text',
  description: 'description',
};

export const STEP_FIELD_MAP: Record<EditableField, StringKeysOf<EpisodeTextImportStep>> = {
  nodeOrSpeaker: 'speakerName',
  text: 'speakerText',
  description: 'description',
};
